(() => {
  'use strict';

  const surfaces = {
    lacquered: 'Madera laqueada',
    oiled: 'Madera aceitada',
    vinyl: 'Piso vinílico',
    unknown: 'Superficie por identificar'
  };
  const needs = {
    routine: 'Cuidado cotidiano',
    stain: 'Mancha puntual',
    dull: 'Aspecto y desgaste',
    damage: 'Daño o humedad'
  };

  function recommendation(surface, need) {
    if (need === 'damage') return {
      title: 'Primero, entender qué pasó.',
      intro: 'Las tablas hinchadas, sueltas o dañadas necesitan una evaluación. Un limpiador no resuelve ese problema.',
      steps: [
        'Si hay un derrame, absorbé el líquido con un paño suave. Evitá seguir mojando el piso.',
        'No lijes, pegues ni apliques renovadores sobre el sector antes de conocer la causa.',
        'Consultanos con una foto general, otra del detalle y, si la tenés, la ficha del piso.'
      ],
      caution: surface === 'vinyl' ? 'El vinílico no se restaura mediante pulido ni hidrolaqueado.' : 'Si la humedad sigue apareciendo, hay que resolver su origen antes de intervenir el acabado.'
    };

    if (surface === 'unknown') return {
      title: 'Conocerlo es el primer cuidado.',
      intro: 'El brillo o el color no alcanzan para identificar el material y su terminación. Por ahora, mantené una rutina en seco.',
      steps: [
        'Retirá el polvo con una mopa suave y seca, sin frotar con fuerza.',
        'Buscá la factura o la ficha del piso. Una foto general y otra de cerca también ayudan al equipo.',
        'Confirmá el material y el acabado con un asesor antes de elegir un limpiador o renovador.'
      ],
      caution: 'Si hay una mancha, absorbé el excedente sin abrasión. Evitá recetas caseras y productos nuevos hasta identificar la superficie.'
    };

    if (need === 'stain') return {
      title: 'Empezá con suavidad.',
      intro: 'Una marca puntual necesita cuidado localizado. Si no cede, seguir probando productos puede afectar la terminación.',
      steps: [
        'Retirá el excedente con un paño suave, sin raspar ni extender la mancha.',
        surface === 'oiled' ? 'Usá únicamente el limpiador aprobado para tu sistema de aceite, siguiendo su etiqueta y con poca humedad.' : 'Usá el limpiador compatible que ya indicaba la ficha de tu piso, con microfibra y muy poca humedad.',
        'Si la marca persiste o el aspecto cambia, detenete y consultanos con una foto del sector.'
      ],
      caution: 'No mezcles productos ni aumentes la concentración salvo que la etiqueta lo indique. Evitá vapor y abrasivos.'
    };

    if (need === 'dull') return {
      title: surface === 'vinyl' ? 'Revisemos su superficie.' : 'Antes de renovar, mirémoslo.',
      intro: 'La opacidad puede tener distintas causas. Primero hay que distinguir suciedad, residuos y desgaste del acabado.',
      steps: [
        'Revisá qué producto venís usando y si respetás la dosis de la etiqueta.',
        surface === 'oiled' ? 'Identificá el aceite original antes de aplicar un mantenimiento. No hay un intervalo universal de renovación.' : 'No superpongas ceras ni renovadores para recuperar el brillo sin confirmar la compatibilidad.',
        'Consultanos con una foto y el nombre del piso o del acabado para definir el próximo paso.'
      ],
      caution: surface === 'vinyl' ? 'El vinílico no admite pulido ni hidrolaqueado. La solución depende del tipo de superficie y del daño.' : 'Un renovador compatible puede mejorar el aspecto; no corrige rayones profundos ni reemplaza una restauración.'
    };

    const routine = {
      lacquered: {
        intro: 'Una rutina suave para madera protegida con poliuretano, sin cera ni aceite.',
        clean: 'Limpiá con una mopa de microfibra y un producto para madera laqueada. Aplicá poca cantidad, según su etiqueta.',
        caution: 'Si tiene cera, aceite o no conocés la protección, volvé a elegir “No estoy seguro” antes de usar este producto.'
      },
      oiled: {
        intro: 'Cuidá el acabado con un producto formulado para tu sistema de aceite, aceite-cera o hardwax oil.',
        clean: 'Pasá una mopa suave con el limpiador para madera aceitada, respetando la dosis y con poca humedad.',
        caution: 'Confirmá las indicaciones de tu sistema de aceite. Renovar con aceite o hacer una limpieza intensiva requiere una evaluación distinta.'
      },
      vinyl: {
        intro: 'Para vinílicos sellados y sin cera, la limpieza cotidiana puede ser simple y con poca humedad.',
        clean: 'Usá un limpiador compatible de pH neutro y una mopa bien escurrida. Respetá la cantidad de la etiqueta.',
        caution: 'Evitá vapor, abrasivos y agua acumulada. Que el producto tolere humedad no significa que deba quedar mojado.'
      }
    }[surface];

    return {
      title: 'Una rutina simple, bien elegida.',
      intro: routine.intro,
      steps: [
        'Retirá primero el polvo y las partículas sueltas con una mopa seca o una aspiradora con cepillo suave.',
        routine.clean,
        'Retirá los derrames al momento y mantené limpia la microfibra para no arrastrar suciedad.'
      ],
      caution: routine.caution,
      product: surface
    };
  }

  function bind(root) {
    if (root.dataset.maintenanceBound === 'true') return;
    const wizard = root.querySelector('[data-maintenance-wizard]');
    const form = root.querySelector('[data-maintenance-form]');
    const surfaceStep = root.querySelector('[data-maintenance-step="surface"]');
    const needStep = root.querySelector('[data-maintenance-step="need"]');
    const result = root.querySelector('[data-maintenance-result]');
    if (!wizard || !form || !surfaceStep || !needStep || !result) return;
    root.dataset.maintenanceBound = 'true';
    wizard.hidden = false;
    const next = root.querySelector('[data-maintenance-next]');
    const submit = root.querySelector('[data-maintenance-submit]');
    const selection = (name) => form.querySelector(`input[name="${name}"]:checked`)?.value || '';
    const focus = (node) => {
      if (!node) return;
      node.focus({ preventScroll: true });
      const bounds = node.getBoundingClientRect();
      if (bounds.top < 24 || bounds.bottom > window.innerHeight - 24) node.scrollIntoView({ block: 'start', behavior: 'auto' });
    };

    function showStep(step, moveFocus = true) {
      result.hidden = true;
      form.hidden = false;
      surfaceStep.hidden = step !== 'surface';
      needStep.hidden = step !== 'need';
      if (moveFocus) focus((step === 'surface' ? surfaceStep : needStep).querySelector('legend'));
    }

    form.addEventListener('change', (event) => {
      if (event.target.name === 'surface') {
        form.querySelectorAll('input[name="need"]').forEach((input) => { input.checked = false; });
      }
      next.disabled = !Object.hasOwn(surfaces, selection('surface'));
      submit.disabled = !Object.hasOwn(needs, selection('need'));
    });

    next.addEventListener('click', () => {
      if (Object.hasOwn(surfaces, selection('surface'))) showStep('need');
    });
    root.querySelector('[data-maintenance-back]').addEventListener('click', () => showStep('surface'));
    root.querySelector('[data-maintenance-restart]').addEventListener('click', () => showStep('surface'));

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const surface = selection('surface');
      const need = selection('need');
      if (!Object.hasOwn(surfaces, surface)) return showStep('surface');
      // Enter on the first question advances without publishing an incomplete result.
      if (!surfaceStep.hidden || !Object.hasOwn(needs, need)) return showStep('need');
      const answer = recommendation(surface, need);
      root.querySelector('[data-maintenance-summary]').textContent = `${surfaces[surface]} · ${needs[need]}`;
      root.querySelector('[data-maintenance-result-title]').textContent = answer.title;
      root.querySelector('[data-maintenance-intro]').textContent = answer.intro;
      root.querySelector('[data-maintenance-caution]').textContent = answer.caution;
      const parameters = new URLSearchParams(window.location.search);
      const productName = (parameters.get('producto_nombre') || '').replace(/\s+/g, ' ').trim().slice(0, 160);
      const productPath = (parameters.get('producto') || '').slice(0, 250);
      const context = [`Mi piso: ${surfaces[surface]}. Necesito ayuda con: ${needs[need]}.`];
      if (productName) context.push(`Producto de referencia: ${productName}.`);
      if (/^\/products\/[a-z0-9-]+$/.test(productPath)) context.push(`Ficha de referencia: https://mrparquet.com.uy${productPath}`);
      context.push('Quisiera confirmar el cuidado adecuado para mi piso.');
      root.querySelector('[data-maintenance-contact]').href = `/pages/contactanos?motivo=mantenimiento&consulta=${encodeURIComponent(context.join('\n'))}`;
      const list = root.querySelector('[data-maintenance-routine]');
      list.replaceChildren(...answer.steps.map((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        return item;
      }));
      const slot = root.querySelector('[data-maintenance-product-slot]');
      slot.replaceChildren();
      if (answer.product) {
        const product = root.querySelector(`template[data-maintenance-product="${answer.product}"]`);
        if (product) slot.append(product.content.cloneNode(true));
      }
      form.hidden = true;
      result.hidden = false;
      focus(root.querySelector('[data-maintenance-result-title]'));
    });

    // Preserve existing links to each material without hiding the rest of the guide.
    function openLinkedGuide() {
      const guide = Array.from(root.querySelectorAll('.at-maintenance__accordion details[id]'))
        .find((detail) => `#${detail.id}` === window.location.hash);
      if (guide) guide.open = true;
    }
    openLinkedGuide();
    window.addEventListener('hashchange', openLinkedGuide);
  }

  document.querySelectorAll('[data-at-maintenance]').forEach(bind);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-at-maintenance]').forEach(bind);
  });
})();
